<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { getCrmTemplates } from '../composables/useCrm';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'create']);

const { t } = useI18n();
const crmTemplates = computed(() => getCrmTemplates(t));

const step = ref(1); // 1: Select template, 2: Name & Description form
const selectedTemplate = ref(crmTemplates.value[0]);
const funnelName = ref('');
const funnelDescription = ref('');

watch(
  () => props.isOpen,
  newVal => {
    if (newVal) {
      step.value = 1;
      selectedTemplate.value = crmTemplates.value[0];
      funnelName.value = '';
      funnelDescription.value = '';
    }
  }
);

function selectTemplate(template) {
  selectedTemplate.value = template;
  funnelName.value = template.id === 'empty' ? '' : template.name;
  funnelDescription.value = template.id === 'empty' ? '' : template.description;
  step.value = 2;
}

function handleBack() {
  step.value = 1;
}

function handleClose() {
  emit('close');
}

function handleCreate() {
  if (!funnelName.value.trim()) return;
  emit('create', {
    name: funnelName.value.trim(),
    description: funnelDescription.value.trim(),
    templateId: selectedTemplate.value.id,
  });
  handleClose();
}
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4"
    @click.self="handleClose"
  >
    <!-- Modal Card -->
    <div
      class="w-full max-w-2xl rounded-2xl border border-n-weak bg-n-surface-1 shadow-2xl overflow-hidden transition-all"
    >
      <!-- STEP 1: Select Template -->
      <div v-if="step === 1" class="p-6">
        <h2 class="text-lg font-extrabold text-n-slate-12 mb-6">
          {{ t('CRM.CREATE_FUNNEL_MODAL.SELECT_TEMPLATE_TITLE') }}
        </h2>

        <!-- Templates Grid (2x2) -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <button
            v-for="template in crmTemplates"
            :key="template.id"
            type="button"
            class="flex flex-col items-start p-5 rounded-xl border border-n-weak bg-n-solid-1/40 hover:bg-n-solid-2 hover:border-n-brand text-left transition-all group cursor-pointer"
            @click="selectTemplate(template)"
          >
            <div
              class="flex size-10 items-center justify-center rounded-lg bg-n-solid-3 text-n-slate-12 mb-3 group-hover:bg-n-brand/10 group-hover:text-n-brand transition-colors"
            >
              <Icon :icon="template.icon" class="size-5" />
            </div>
            <h3
              class="text-sm font-extrabold text-n-slate-12 mb-1 group-hover:text-n-brand transition-colors"
            >
              {{ template.name }}
            </h3>
            <p class="text-xs text-n-slate-10 leading-relaxed">
              {{ template.description }}
            </p>
          </button>
        </div>

        <div class="flex justify-end">
          <button
            type="button"
            class="px-4 py-2 text-xs font-semibold text-n-slate-11 hover:text-n-slate-12 cursor-pointer"
            @click="handleClose"
          >
            {{ t('CRM.CREATE_FUNNEL_MODAL.CANCEL') }}
          </button>
        </div>
      </div>

      <!-- STEP 2: Funnel Details (Name & Description) -->
      <div v-else-if="step === 2" class="p-6">
        <h2 class="text-lg font-extrabold text-n-slate-12 mb-6">
          {{ t('CRM.CREATE_FUNNEL_MODAL.TITLE') }}
        </h2>

        <form class="space-y-5" @submit.prevent="handleCreate">
          <!-- Nome Field -->
          <div>
            <label class="block text-xs font-extrabold text-n-slate-12 mb-1.5">
              {{ t('CRM.CREATE_FUNNEL_MODAL.NAME_LABEL') }}
            </label>
            <input
              v-model="funnelName"
              type="text"
              required
              :placeholder="t('CRM.CREATE_FUNNEL_MODAL.NAME_PLACEHOLDER')"
              class="reset-base !mb-0 w-full h-10 rounded-xl border border-n-weak bg-n-solid-1 px-3 text-xs text-n-slate-12 shadow-sm focus:border-n-brand focus:outline-none"
            />
          </div>

          <!-- Descrição Field -->
          <div>
            <label class="block text-xs font-extrabold text-n-slate-12 mb-1.5">
              {{ t('CRM.CREATE_FUNNEL_MODAL.DESCRIPTION_LABEL') }}
            </label>
            <div class="relative">
              <textarea
                v-model="funnelDescription"
                rows="4"
                maxlength="2000"
                :placeholder="
                  t('CRM.CREATE_FUNNEL_MODAL.DESCRIPTION_PLACEHOLDER')
                "
                class="w-full rounded-xl border border-n-weak bg-n-solid-1 p-3 text-xs text-n-slate-12 shadow-sm focus:border-n-brand focus:outline-none resize-none"
              />
              <span
                class="absolute bottom-2.5 right-3 text-[11px] font-medium text-n-slate-10"
              >
                {{ funnelDescription.length }} / 2000
              </span>
            </div>
          </div>

          <!-- Actions Footer -->
          <div
            class="flex items-center justify-between pt-4 border-t border-n-weak"
          >
            <button
              type="button"
              class="px-4 py-2 text-xs font-semibold text-n-slate-11 hover:text-n-slate-12 cursor-pointer"
              @click="handleBack"
            >
              {{ t('CRM.CREATE_FUNNEL_MODAL.BACK') }}
            </button>

            <div class="flex items-center gap-2">
              <button
                type="button"
                class="px-4 py-2 text-xs font-semibold text-n-slate-11 hover:text-n-slate-12 cursor-pointer"
                @click="handleClose"
              >
                {{ t('CRM.CREATE_FUNNEL_MODAL.CANCEL') }}
              </button>
              <button
                type="submit"
                :disabled="!funnelName.trim()"
                class="rounded-xl bg-n-brand px-5 py-2.5 text-xs font-extrabold text-[#1c1c1c] shadow hover:brightness-95 disabled:bg-[#719f03] disabled:text-[#1c1c1c]/70 transition-all cursor-pointer"
              >
                {{ t('CRM.CREATE_FUNNEL_MODAL.CREATE_BUTTON') }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
